import { cn } from "@newxsystems/shared";
import { Icon } from "@templates/design-system";
import { TootActions } from "../../../../modules/layout/components/TootActions/TootActions";
import { TootHeading } from "../../../../modules/layout/components/TootHeading/TootHeading";
import { TootSectionShell } from "../../../../modules/layout/components/TootSectionShell/TootSectionShell";
import { TootCard } from "../../../../modules/surface/components/TootCard/TootCard";
import type { IIconFeatureGridBlockProps } from "./IconFeatureGridBlock.props";
import { RichTextHtml } from "../../RichTextHtml";

const gridColumnClasses: Record<3 | 4, string> = {
  3: "xl:grid-cols-3",
  4: "xl:grid-cols-4",
};

export const IconFeatureGridBlock = ({
  anchorId,
  buttons,
  columns,
  eyebrow,
  heading,
  items,
  parentClass,
  spacing,
  subheading,
  variant,
}: IIconFeatureGridBlockProps) => {
  if (!items?.length) {
    return null;
  }

  const isVertical = variant === "vertical";
  const hasHeader = Boolean(heading || eyebrow || subheading);
  const header = hasHeader ? (
    <TootHeading eyebrow={eyebrow} heading={heading} subheading={subheading} />
  ) : null;
  const grid = (
    <div
      className={cn(
        "grid grid-cols-1 items-stretch gap-4 md:grid-cols-2",
        columns && gridColumnClasses[columns],
      )}
    >
      {items.map((item, index) => {
        return (
          <TootCard
            as="article"
            ariaLabel={item.link?.ariaLabel}
            className="h-full border-toot-grid/90 bg-toot-surface-raised/65 transition duration-300 hover:border-orange-400/55 focus-within:border-orange-400/60"
            href={item.link?.href}
            key={index}
            rel={item.link?.target === "_blank" ? "noopener noreferrer" : undefined}
            showAccent={false}
            target={item.link?.target}
            tone="muted"
          >
            <div className="flex h-full flex-col gap-5">
              <div className="flex items-center justify-between gap-3">
                <span
                  aria-hidden="true"
                  className="inline-flex h-11 min-w-11 items-center justify-center rounded-full border border-orange-400/40 bg-orange-500/10 px-3 font-mono text-sm font-black uppercase tracking-[0.14em] text-toot-orange shadow-lg shadow-orange-500/20"
                >
                  <Icon className="size-5" name={item.icon} />
                </span>
                <span className="font-mono text-[0.62rem] font-bold uppercase tracking-[0.24em] text-toot-dim">
                  {String(index + 1).padStart(2, "0")}
                </span>
              </div>
              <div className="space-y-3">
                {item.heading ? (
                  <h3 className="text-xl font-black tracking-[-0.04em] text-toot-foreground">
                    {item.heading}
                  </h3>
                ) : null}
                {item.subheading ? (
                  <RichTextHtml
                    className="text-sm leading-6 text-toot-muted"
                    html={item.subheading}
                  />
                ) : null}
              </div>
              {item.link?.href && item.link.text ? (
                <span className="mt-auto inline-flex items-center gap-2 pt-2 font-mono text-xs font-black uppercase tracking-[0.18em] text-toot-orange">
                  {item.link.text}
                  <span aria-hidden="true">↗</span>
                </span>
              ) : null}
            </div>
          </TootCard>
        );
      })}
    </div>
  );

  return (
    <TootSectionShell
      actions={buttons?.length ? <TootActions actions={buttons} /> : undefined}
      className={cn(
        "space-y-0",
        !isVertical &&
          hasHeader &&
          "grid min-w-0 gap-8 lg:grid-cols-[minmax(15rem,0.38fr)_minmax(0,1fr)] lg:gap-12 xl:gap-16",
      )}
      id={anchorId ?? undefined}
      rootClassName={parentClass}
      spacing={spacing ?? undefined}
      width="wide"
      header={isVertical ? header : null}
    >
      {!isVertical && header ? <div className="min-w-0 lg:max-w-[26rem]">{header}</div> : null}
      {grid}
    </TootSectionShell>
  );
};

IconFeatureGridBlock.displayName = "Block::IconFeatureGridBlock";
